// Enhanced data loader with progressive loading, compression, and smart caching
export const CHUNK_SIZE = 25; // Reduced chunk size for better performance
export const MAX_CONCURRENT_REQUESTS = 3; // Limit concurrent requests to prevent overwhelming the browser

// Data compression and optimization utilities
export const compressData = (data) => {
  // Remove unnecessary fields and compress data structure
  return data.map(location => ({
    d: location.date,
    l: {
      n: location.location_of_servers.location_name,
      lat: parseFloat(location.location_of_servers.latitude),
      lng: parseFloat(location.location_of_servers.longitude)
    },
    s: location.servers_at_location.map(server => ({
      p: server.blockchain_pool_name,
      e: server.electricity_usage,
      c: server.co2e_emissions
    })),
    ce: location.co2e_emissions,
    eu: location.electricity_usage
  }));
};

export const decompressData = (compressedData) => {
  // Add defensive programming to handle edge cases
  if (!compressedData) {
    console.error('decompressData received null/undefined data');
    return [];
  }
  
  if (!Array.isArray(compressedData)) {
    console.error('decompressData received non-array data:', compressedData);
    console.error('Data type:', typeof compressedData);
    
    // Try to handle different data formats
    if (compressedData && typeof compressedData === 'object') {
      // If it's an object with a data property, try to extract it
      if (Array.isArray(compressedData.data)) {
        console.log('Extracted data from compressedData.data property');
        return decompressData(compressedData.data);
      }
      
      // If it's already decompressed data, return it wrapped in array
      if (compressedData.location_of_servers && compressedData.servers_at_location) {
        console.log('Data appears to be already decompressed, wrapping in array');
        return [compressedData];
      }
    }
    
    // Return empty array as fallback
    console.warn('Returning empty array as fallback');
    return [];
  }
  
  // Ensure we have valid compressed data
  if (compressedData.length === 0) {
    console.log('decompressData received empty array');
    return [];
  }
  
  // Validate first item to ensure it has the expected compressed structure
  const firstItem = compressedData[0];
  if (!firstItem || !firstItem.d || !firstItem.l || !firstItem.s) {
    console.error('Compressed data structure validation failed. First item:', firstItem);
    console.error('Expected structure: { d: date, l: location, s: servers }');
    return [];
  }
  
  return compressedData.map(item => ({
    date: item.d,
    location_of_servers: {
      location_name: item.l.n,
      latitude: item.l.lat.toString(),
      longitude: item.l.lng.toString()
    },
    servers_at_location: item.s.map(server => ({
      blockchain_pool_name: server.p,
      electricity_usage: server.e,
      co2e_emissions: server.c
    })),
    co2e_emissions: item.ce,
    electricity_usage: item.eu
  }));
};

// Progressive data loading with request throttling
export const loadDataProgressive = async (dateRange, onProgress, onChunkLoaded) => {
  const { startDate, endDate } = dateRange;
  const dateChunks = generateDateChunks(startDate, endDate);
  const totalChunks = dateChunks.length;
  let loadedChunks = 0;
  let allData = [];

  // Process chunks with controlled concurrency
  for (let i = 0; i < dateChunks.length; i += MAX_CONCURRENT_REQUESTS) {
    const batch = dateChunks.slice(i, i + MAX_CONCURRENT_REQUESTS);
    
    const batchPromises = batch.map(async (dateChunk) => {
      try {
        const data = await fetchDataWithRetry(dateChunk);
        loadedChunks++;
        
        // Report progress
        onProgress && onProgress(loadedChunks / totalChunks);
        
        // Process chunk data (don't compress here, return raw data)
        const processedData = processChunkData(data);
        
        // Store compressed data in memory-efficient way for caching
        const compressedData = compressData(processedData);
        const chunkKey = `chunk_${dateChunk.join('_')}`;
        window.chunkCache = window.chunkCache || {};
        window.chunkCache[chunkKey] = compressedData;
        
        // Callback for each chunk
        onChunkLoaded && onChunkLoaded(processedData, dateChunk);
        
        // Return uncompressed data for immediate use
        return processedData;
      } catch (error) {
        console.error(`Failed to load chunk ${dateChunk}:`, error);
        return [];
      }
    });

    const batchResults = await Promise.all(batchPromises);
    
    // Debug logging
    console.log('🔍 Debug: batchResults:', batchResults);
    console.log('🔍 Debug: batchResults types:', batchResults.map(r => typeof r));
    console.log('🔍 Debug: batchResults are arrays:', batchResults.map(r => Array.isArray(r)));
    
    // Ensure all results are arrays before flattening
    const validResults = batchResults.filter(result => Array.isArray(result));
    if (validResults.length !== batchResults.length) {
      console.warn('🔍 Debug: Some batch results were not arrays, filtering them out');
      console.warn('🔍 Debug: Invalid results:', batchResults.filter(result => !Array.isArray(result)));
    }
    
    // Flatten the valid results
    const flattenedResults = validResults.flat();
    console.log('🔍 Debug: Flattened results length:', flattenedResults.length);
    
    allData.push(...flattenedResults);
    
    // Small delay between batches to prevent UI blocking
    if (i + MAX_CONCURRENT_REQUESTS < dateChunks.length) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  console.log('🔍 Debug: Final allData length:', allData.length);
  return allData;
};

// Generate optimized date chunks (group dates to reduce HTTP requests)
export const generateDateChunks = (startDate, endDate) => {
  console.log('Generating date chunks from:', startDate, 'to:', endDate);
  
  const chunks = [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Group dates into weekly chunks to reduce HTTP requests
  let current = new Date(start);
  while (current <= end) {
    const chunkStart = new Date(current);
    const chunkEnd = new Date(current);
    chunkEnd.setDate(chunkEnd.getDate() + 6); // 7-day chunks
    
    if (chunkEnd > end) {
      chunkEnd.setTime(end.getTime());
    }
    
    const chunkDates = [];
    const tempDate = new Date(chunkStart);
    while (tempDate <= chunkEnd) {
      chunkDates.push(tempDate.toISOString().split('T')[0]);
      tempDate.setDate(tempDate.getDate() + 1);
    }
    
    chunks.push(chunkDates);
    console.log('Generated chunk:', chunkDates);
    current.setDate(current.getDate() + 7);
  }
  
  console.log('Total chunks generated:', chunks.length);
  return chunks;
};

// Fetch data with retry logic and compression
export const fetchDataWithRetry = async (dateChunk, retries = 3) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      console.log(`Attempt ${attempt}: Fetching data for chunk:`, dateChunk);
      
      // If it's a single date, fetch individual file
      if (dateChunk.length === 1) {
        const url = `${process.env.PUBLIC_URL || ''}/resources/data_${dateChunk[0]}.json?_t=${Date.now()}`;
        console.log('Fetching single date from:', url);
        
        const response = await fetch(url, {
          headers: {
            'Accept-Encoding': 'gzip, deflate',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log(`🔍 Debug: Successfully loaded data for date ${dateChunk[0]}:`, data);
        console.log(`🔍 Debug: Data type:`, typeof data);
        console.log(`🔍 Debug: Data is array:`, Array.isArray(data));
        if (Array.isArray(data)) {
          console.log(`🔍 Debug: Data length:`, data.length);
        }
        return data;
      }
      
      // For multiple dates, try to fetch a combined endpoint if available
      // Fallback to individual requests
      console.log('Fetching multiple dates:', dateChunk);
      
      const responses = await Promise.all(
        dateChunk.map(async (date) => {
          const url = `${process.env.PUBLIC_URL || ''}/resources/data_${date}.json?_t=${Date.now()}`;
          console.log(`Fetching date ${date} from:`, url);
          
          const response = await fetch(url, {
            headers: {
              'Accept-Encoding': 'gzip, deflate',
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            }
          });
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText} for date ${date}`);
          }
          
          return response;
        })
      );
      
            const results = await Promise.all(
        responses.map(async (response, index) => {
          const data = await response.json();
          console.log(`🔍 Debug: Successfully loaded data for date ${dateChunk[index]}:`, data);
          console.log(`🔍 Debug: Data type:`, typeof data);
          console.log(`🔍 Debug: Data is array:`, Array.isArray(data));
          if (Array.isArray(data)) {
            console.log(`🔍 Debug: Data length:`, data.length);
          }
          return data;
        })
      );

      const flattenedResults = results.flat();
      console.log(`🔍 Debug: Total records loaded for chunk:`, flattenedResults.length);
      console.log(`🔍 Debug: Flattened results type:`, typeof flattenedResults);
      console.log(`🔍 Debug: Flattened results is array:`, Array.isArray(flattenedResults));
      return flattenedResults;
      
    } catch (error) {
      console.error(`Attempt ${attempt} failed for chunk ${dateChunk}:`, error);
      
      if (attempt === retries) {
        throw error;
      }
      
      // Exponential backoff
      const delay = Math.pow(2, attempt) * 1000;
      console.log(`Waiting ${delay}ms before retry...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

// Process chunk data efficiently
export const processChunkData = (data) => {
  // Add defensive programming to handle cases where data might not be an array
  if (!Array.isArray(data)) {
    console.error('processChunkData received non-array data:', data);
    console.error('Data type:', typeof data);
    console.error('Data structure:', JSON.stringify(data, null, 2));
    
    // Try to handle different data formats
    if (data && typeof data === 'object') {
      // If it's an object with a data property, try to extract it
      if (Array.isArray(data.data)) {
        console.log('Extracted data from data.data property');
        return processChunkData(data.data);
      }
      
      // If it's a single location object, wrap it in an array
      if (data.location_of_servers && data.servers_at_location) {
        console.log('Wrapping single location object in array');
        return processChunkData([data]);
      }
    }
    
    // Return empty array as fallback
    console.warn('Returning empty array as fallback');
    return [];
  }
  
  // Ensure data has the expected structure
  if (data.length === 0) {
    console.log('processChunkData received empty array');
    return [];
  }
  
  // Validate first item to ensure it has the expected structure
  const firstItem = data[0];
  if (!firstItem || !firstItem.servers_at_location || !Array.isArray(firstItem.servers_at_location)) {
    console.error('Data structure validation failed. First item:', firstItem);
    console.error('Expected structure: { servers_at_location: Array, location_of_servers: Object }');
    return [];
  }
  
  return data.map(location => {
    // Pre-calculate and cache computed values
    const totalElectricity = location.servers_at_location.reduce((sum, server) => sum + server.electricity_usage, 0);
    const totalCO2 = location.servers_at_location.reduce((sum, server) => sum + server.co2e_emissions, 0);
    
    return {
      ...location,
      location_of_servers: {
        ...location.location_of_servers,
        longitude: Number(location.location_of_servers.longitude),
        latitude: Number(location.location_of_servers.latitude)
      },
      // Pre-compute pools set for faster filtering
      pools: new Set(location.servers_at_location.map(server => server.blockchain_pool_name)),
      // Cache computed totals
      totalElectricity,
      totalCO2
    };
  });
};

// Memory-efficient data filtering
export const filterDataEfficiently = (data, filters, viewOption) => {
  if (!data || data.length === 0) return [];
  
  // Use Web Workers for heavy filtering if available
  if (window.Worker && filters.complex) {
    return filterWithWorker(data, filters, viewOption);
  }
  
  // Efficient in-memory filtering
  return data.filter(location => {
    // Apply filters progressively to short-circuit early
    if (filters.dateRange) {
      const locationDate = new Date(location.date);
      if (locationDate < filters.dateRange.start || locationDate > filters.dateRange.end) {
        return false;
      }
    }
    
    if (filters.pools && filters.pools.length > 0) {
      const hasMatchingPool = location.servers_at_location.some(server => 
        filters.pools.includes(server.blockchain_pool_name)
      );
      if (!hasMatchingPool) return false;
    }
    
    return true;
  });
};

// Web Worker for complex filtering (if available)
const filterWithWorker = (data, filters, viewOption) => {
  return new Promise((resolve) => {
    const worker = new Worker(URL.createObjectURL(new Blob([`
      self.onmessage = function(e) {
        const { data, filters, viewOption } = e.data;
        
        // Perform complex filtering in background
        const filtered = data.filter(location => {
          // Complex filtering logic here
          return true; // Simplified for example
        });
        
        self.postMessage(filtered);
      };
    `])));
    
    worker.onmessage = (e) => {
      resolve(e.data);
      worker.terminate();
    };
    
    worker.postMessage({ data, filters, viewOption });
  });
};

// Clean up memory when switching views
export const cleanupChunkCache = () => {
  if (window.chunkCache) {
    // Keep only recent chunks in memory
    const chunkKeys = Object.keys(window.chunkCache);
    if (chunkKeys.length > 10) { // Keep only last 10 chunks
      const keysToRemove = chunkKeys.slice(0, chunkKeys.length - 10);
      keysToRemove.forEach(key => delete window.chunkCache[key]);
    }
  }
};

// Test function to verify data loading works
export const testDataLoading = async () => {
  console.log('Testing data loading...');
  
  try {
    // Test single date loading
    const singleDateData = await fetchDataWithRetry(['2021-01-01'], 1);
    console.log('Single date test successful:', singleDateData.length, 'records');
    
    // Test chunk loading
    const chunkData = await fetchDataWithRetry(['2021-01-01', '2021-01-02'], 1);
    console.log('Chunk test successful:', chunkData.length, 'records');
    
    return {
      success: true,
      singleDate: singleDateData.length,
      chunk: chunkData.length
    };
  } catch (error) {
    console.error('Data loading test failed:', error);
    return {
      success: false,
      error: error.message
    };
  }
}; 